import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';
import { ReportScopeFilter } from 'src/components/report-filter/ReportScopeFilter';
import { useReportScope } from 'src/routes/context/ReportScopeContext';

import { downloadBlob } from 'src/utils/download-file';

import {
  getBranchScorecard,
  exportBranchScorecard,
  type ExportFormat,
  type BranchScorecardRow,
} from 'src/api/reports.api';

const MEDAL_COLOR = ['#FFB300', '#9E9E9E', '#B87333'];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function attColor(rate: number): 'success' | 'info' | 'warning' | 'error' {
  if (rate >= 90) return 'success';
  if (rate >= 75) return 'info';
  if (rate >= 50) return 'warning';
  return 'error';
}

export function BranchScorecardView() {
  const { companyId, branchId } = useReportScope();
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<BranchScorecardRow[] | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  const handleFetch = async () => {
    setError(null);
    setLoading(true);
    try {
      setRows(await getBranchScorecard(companyId, branchId, start, end));
    } catch (e) {
      setError('Rapor yüklenemedi. Yetki veya bağlantı sorununu kontrol edin.');
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const blob = await exportBranchScorecard(companyId, branchId, start, end, format);
      downloadBlob(blob, `sube-karnesi-${start}_${end}`, format);
    } catch (e) {
      setError('Dışa aktarma başarısız oldu.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <DashboardContent>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4">Şube Karnesi</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Şubeleri devam oranı ve ortalama düzenlilik skoruna göre sıralar (en iyi → en kötü).
        </Typography>
      </Box>

      <Card sx={{ p: 3, mb: 3 }}>
        <ReportScopeFilter />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField size="small" type="date" label="Başlangıç" InputLabelProps={{ shrink: true }}
            value={start} onChange={(e) => setStart(e.target.value)} />
          <TextField size="small" type="date" label="Bitiş" InputLabelProps={{ shrink: true }}
            value={end} onChange={(e) => setEnd(e.target.value)} />
          <Button variant="contained" onClick={handleFetch} disabled={loading}>
            {loading ? 'Yükleniyor…' : 'Raporu Getir'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" color="error" startIcon={<Iconify icon="solar:file-text-bold" />}
            disabled={!rows || exporting !== null} onClick={() => handleExport('PDF')}>
            {exporting === 'PDF' ? 'PDF…' : 'PDF'}
          </Button>
          <Button variant="outlined" color="success" startIcon={<Iconify icon="solar:file-download-bold" />}
            disabled={!rows || exporting !== null} onClick={() => handleExport('EXCEL')}>
            {exporting === 'EXCEL' ? 'Excel…' : 'Excel'}
          </Button>
        </Stack>
        {error && <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>{error}</Typography>}
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && !loading && (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56 }}>Sıra</TableCell>
                  <TableCell>Şube</TableCell>
                  <TableCell align="center">Çalışan</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Devam Oranı</TableCell>
                  <TableCell align="center">Ort. Skor</TableCell>
                  <TableCell align="center">Devamsızlık</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((b) => (
                  <TableRow key={b.branchId} hover>
                    <TableCell>
                      {b.rank <= 3 ? (
                        <Iconify icon="solar:medal-ribbon-star-bold-duotone" width={22}
                          sx={{ color: MEDAL_COLOR[b.rank - 1] }} />
                      ) : (
                        b.rank
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{b.branchName}</TableCell>
                    <TableCell align="center">{b.employeeCount}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress variant="determinate" value={Math.min(100, b.attendanceRate)}
                          color={attColor(b.attendanceRate)} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
                        <Chip size="small" color={attColor(b.attendanceRate)} label={`%${b.attendanceRate}`} />
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{b.avgRegularityScore}</TableCell>
                    <TableCell align="center">{b.absenceCount}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      Bu kapsam/dönem için şube verisi bulunamadı.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </DashboardContent>
  );
}
